import { Pagination } from "services/search";
import { createApiCall } from "services/api/createApiCall";
import {
  AddChannelImageRequest,
  CreateListingRequest,
  ListingVehiclesSearchResult,
  MarketplaceDetailsRequest,
  UpdateListingChannelRequest,
  UpdateListingRequest,
  VehicleListing,
} from "./types";
import { transformListing, transformListingVehiclesSearchResult } from "./transformations";


/*---------------------------------------------------------------------------------------
  LISTING VEHICLES - GET (vehicles + listings in one call)
-----------------------------------------------------------------------------------------*/
export const getListingVehiclesAPI = async (
  pagination: Pagination,
  name?: string | null,
  vehicleManufacturerId?: string | null,
  vehicleModelId?: string | null,
  vehicleTypeId?: string | null,
  publishedFilter?: string | null
): Promise<ListingVehiclesSearchResult> => {
  let path = `/v1/sales/listings/vehicles?name=`;
  if (name) path += `${name}`;
  if (vehicleManufacturerId) path += `&vehicleManufacturerId=${vehicleManufacturerId}`;
  if (vehicleModelId) path += `&vehicleModelId=${vehicleModelId}`;
  if (vehicleTypeId) path += `&vehicleTypeId=${vehicleTypeId}`;
  if (publishedFilter) path += `&publishedFilter=${publishedFilter}`;

  path += `&pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}`;
  if (pagination.sort?.sortBy) path += `&orderBy=${pagination.sort.sortBy}`;
  if (pagination.sort?.direction) path += `&ascending=${pagination.sort.direction === "asc"}`;

  const response = await createApiCall(
    { path, method: "GET" },
    { getMessage: { error: (error: any) => `${error.response?.data?.message}` } }
  )();

  return transformListingVehiclesSearchResult(response.data);
};

/*---------------------------------------------------------------------------------------
  LISTING - GET BY ID
-----------------------------------------------------------------------------------------*/
export const getListingAPI = async (id: string): Promise<VehicleListing> => {
  const response = await createApiCall(
    { path: `/v1/sales/listings/${id}`, method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return transformListing(response.data);
};

/*---------------------------------------------------------------------------------------
  LISTING - CREATE
-----------------------------------------------------------------------------------------*/
export const createListingAPI = async (body: CreateListingRequest): Promise<VehicleListing> => {
  const response = await createApiCall(
    { path: "/v1/sales/listings", method: "POST", body },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return transformListing(response.data);
};

/*---------------------------------------------------------------------------------------
  LISTING - UPDATE
-----------------------------------------------------------------------------------------*/
export const updateListingAPI = async (id: string, body: UpdateListingRequest): Promise<VehicleListing> => {
  const response = await createApiCall(
    { path: `/v1/sales/listings/${id}`, method: "PUT", body },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return transformListing(response.data);
};

/*---------------------------------------------------------------------------------------
  MARKETPLACE LISTING DETAILS - UPDATE (upsert)
-----------------------------------------------------------------------------------------*/
export const updateMarketplaceDetailsAPI = async (
  listingId: string,
  body: MarketplaceDetailsRequest
): Promise<any> => {
  const response = await createApiCall(
    { path: `/v1/sales/listings/${listingId}/marketplace-details`, method: "PUT", body },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return response.data;
};

/*---------------------------------------------------------------------------------------
  LISTING CHANNEL - UPDATE
-----------------------------------------------------------------------------------------*/
export const updateListingChannelAPI = async (
  listingId: string,
  channelId: string,
  body: UpdateListingChannelRequest
): Promise<any> => {
  const response = await createApiCall(
    { path: `/v1/sales/listings/${listingId}/channels/${channelId}`, method: "PUT", body },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return response.data;
};

/*---------------------------------------------------------------------------------------
  LISTING CHANNEL IMAGE - ADD
-----------------------------------------------------------------------------------------*/
export const addChannelImageAPI = async (
  listingId: string,
  channelId: string,
  body: AddChannelImageRequest
): Promise<any> => {
  const response = await createApiCall(
    { path: `/v1/sales/listings/${listingId}/channels/${channelId}/images`, method: "POST", body },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return response.data;
};

/*---------------------------------------------------------------------------------------
  LISTING CHANNEL IMAGE - REMOVE
-----------------------------------------------------------------------------------------*/
export const removeChannelImageAPI = async (
  listingId: string,
  channelId: string,
  imageId: string
): Promise<any> => {
  const response = await createApiCall(
    { path: `/v1/sales/listings/${listingId}/channels/${channelId}/images/${imageId}`, method: "DELETE" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return response.data;
};

/*---------------------------------------------------------------------------------------
  LISTING - GET BY VEHICLE
-----------------------------------------------------------------------------------------*/
export const getListingByVehicleAPI = async (fleetVehicleId: string): Promise<VehicleListing> => {
  const response = await createApiCall(
    { path: `/v1/sales/listings/by-vehicle/${fleetVehicleId}`, method: "GET" },
    { getMessage: { error: (error: any) => `${error.response?.data?.message}` } }
  )();
  return transformListing(response.data);
};
